import React from 'react';
import { Drawer, Image, Spin, Button, Popconfirm, Alert } from 'antd';
import { Trash2, Store, User, Calendar, ScanBarcode, Image as ImageIcon, CircleDollarSign, ArrowLeftRight, Link2Off } from 'lucide-react';
import { fmtVZ } from '../../../utils/timeUtils';
import { formatPriceName } from '../../../utils/priceName';
import { EmptyState, StatusTag } from '../../common';
import AuditStatusBadge from './AuditStatusBadge';
import AuditFlagChips, { BarcodeIcon } from './AuditFlagChips';
import AuditPriceDisplay, { formatBs } from './AuditPriceDisplay';
import { useAuditRecord } from './auditDrawers.controller';

function InfoTile({ icon, label, value, sub }) {
    return (
        <div className="h-full rounded-xl border border-surface-200 bg-white p-4">
            <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-100 text-surface-500 text-[15px]">{icon}</span>
                <div className="min-w-0 flex-1">
                    <div className="mb-0.5 text-xs font-medium text-surface-500">{label}</div>
                    <div className="truncate text-sm font-semibold text-surface-900">{value || '—'}</div>
                    {sub && <div className="mt-0.5 truncate text-xs text-surface-500">{sub}</div>}
                </div>
            </div>
        </div>
    );
}

/** White block inside the drawer with an optional icon + title row. */
function DrawerSection({ icon, title, extra, children, flush = false }) {
    return (
        <section className="overflow-hidden rounded-xl border border-surface-200 bg-white">
            {title && (
                <div className="flex items-center justify-between gap-3 border-b border-surface-100 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-surface-900">
                        <span className="flex text-[15px] text-surface-400">{icon}</span>
                        {title}
                    </div>
                    {extra}
                </div>
            )}
            <div className={flush ? '' : 'p-4'}>{children}</div>
        </section>
    );
}

export default function AuditRecordDrawer({
    open,
    onClose,
    recordId,
    onFetchRecord,
    onDelete,
    onCorrectCurrency,
    deleting,
    correctingCurrency,
}) {
    const { loading, record, images, productName, hasFlags, barcodeInvalid, canCorrectToBs, isBs, hint, handleCorrectCurrency } = useAuditRecord({
        open,
        recordId,
        onFetchRecord,
        onCorrectCurrency,
    });

    return (
        <Drawer
            title="Detalle de escaneo"
            width={680}
            open={open}
            onClose={onClose}
            destroyOnClose
            styles={{ body: { padding: 0 } }}
            extra={
                record &&
                onDelete && (
                    <Popconfirm
                        title="¿Eliminar este escaneo?"
                        description="Esta acción no se puede deshacer."
                        onConfirm={() => onDelete?.(record)}
                        okText="Sí, eliminar"
                        okType="danger"
                        cancelText="Cancelar"
                        getPopupContainer={() => document.body}>
                        <Button danger icon={<Trash2 />} loading={deleting} size="small">
                            Eliminar
                        </Button>
                    </Popconfirm>
                )
            }>
            {loading ? (
                <div className="flex justify-center items-center py-24">
                    <Spin />
                </div>
            ) : !record ? (
                <EmptyState icon={<ScanBarcode />} title="Sin datos del escaneo" description="No se encontró la información de este escaneo." />
            ) : (
                <div className="min-h-full bg-surface-50">
                    <div className="border-b border-surface-100 bg-white px-6 pt-5 pb-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                                <h3 className="m-0 mb-1.5 truncate text-lg font-semibold tracking-tight text-surface-900">{productName || record.code}</h3>
                                <div className="flex flex-wrap items-center gap-2">
                                    <BarcodeIcon barcodeMeta={record.barcodeMeta} hasCatalogMatch={!!record.auditProduct} />
                                    <span className="text-sm text-surface-500 tabular-nums">{record.code}</span>
                                    {record.barcodeMeta?.format && <StatusTag tone="neutral">{record.barcodeMeta.format}</StatusTag>}
                                </div>
                            </div>
                            <AuditStatusBadge type="image" status={record.imageProcessingStatus} error={record.imageProcessingError} />
                        </div>
                        {(hasFlags || barcodeInvalid) && (
                            <div className="mt-3">
                                <AuditFlagChips flags={record.flags} barcodeMeta={record.barcodeMeta} />
                            </div>
                        )}
                    </div>

                    <div className="space-y-4 p-6">
                        <DrawerSection
                            icon={<ImageIcon />}
                            title="Fotos del anaquel"
                            extra={
                                <span className="text-xs text-surface-500 tabular-nums">
                                    {record.imageCount ?? images.length} imagen{(record.imageCount ?? images.length) !== 1 ? 'es' : ''}
                                </span>
                            }>
                            {images.length ? (
                                <Image.PreviewGroup>
                                    {images.length === 1 ? (
                                        <Image
                                            src={images[0]}
                                            alt="Foto del producto"
                                            className="rounded-lg object-cover"
                                            style={{ width: '100%', maxHeight: 320 }}
                                            fallback="/not-found.png"
                                        />
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3">
                                            {images.map((src, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`relative overflow-hidden rounded-lg border border-surface-200 ${idx === 0 ? 'col-span-2' : ''}`}>
                                                    <Image
                                                        src={src}
                                                        alt={`Imagen ${idx + 1}`}
                                                        width="100%"
                                                        height={idx === 0 ? 220 : 140}
                                                        className="object-cover"
                                                        fallback="/not-found.png"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Image.PreviewGroup>
                            ) : (
                                <EmptyState icon={<ImageIcon />} title="Sin imágenes disponibles" className="!py-6" />
                            )}
                        </DrawerSection>

                        <DrawerSection icon={<CircleDollarSign />} title="Precio registrado">
                            <div className="mb-4 flex flex-wrap items-center gap-2">
                                <StatusTag tone="info">Moneda: {record.currency || 'dollars'}</StatusTag>
                                <StatusTag tone="neutral">Monto ingresado: {formatBs(record.price) || record.price}</StatusTag>
                            </div>
                            <div>
                                <div className="mb-1 text-xs font-medium text-surface-500">Equivalente en USD</div>
                                <AuditPriceDisplay
                                    size="large"
                                    price={record.price}
                                    currency={record.currency}
                                    priceUsd={record.priceUsd}
                                    priceConversion={record.priceConversion}
                                    isPromo={record.isPromo}
                                    promoPrice={record.promoPrice}
                                    promoPriceUsd={record.promoPriceUsd}
                                    promoPriceConversion={record.promoPriceConversion}
                                />
                            </div>

                            {canCorrectToBs && (
                                <div className="mt-4 border-t border-surface-100 pt-4">
                                    {record.suspectedBsMislabel && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            className="mb-3"
                                            message="Posible error de moneda"
                                            description={hint?.reason || 'El auditor pudo haber ingresado bolívares pero seleccionó dólares.'}
                                        />
                                    )}
                                    <p className="m-0 mb-3 text-xs text-surface-500">
                                        Si el monto ingresado ({record.price}) es en bolívares, corrige la moneda para convertir a USD con la tasa BCV del{' '}
                                        {fmtVZ(record.createdAt)}.
                                        {hint?.impliedUsdIfBs != null && (
                                            <span className="mt-1 block font-medium text-warning-fg tabular-nums">
                                                Conversión estimada: ~${hint.impliedUsdIfBs.toFixed(2)} USD
                                            </span>
                                        )}
                                    </p>
                                    {onCorrectCurrency && (
                                        <Popconfirm
                                            title="¿Corregir moneda a bolívares?"
                                            description={`Se guardará como Bs ${record.price} y se recalculará el precio en USD.`}
                                            onConfirm={handleCorrectCurrency}
                                            okText="Sí, es bolívares"
                                            okType="primary"
                                            cancelText="Cancelar"
                                            getPopupContainer={() => document.body}>
                                            <Button
                                                icon={<ArrowLeftRight />}
                                                loading={correctingCurrency}
                                                type={record.suspectedBsMislabel ? 'primary' : 'default'}>
                                                Corregir: es bolívares, no dólares
                                            </Button>
                                        </Popconfirm>
                                    )}
                                </div>
                            )}

                            {isBs && (
                                <Alert
                                    type="success"
                                    showIcon
                                    className="mt-4"
                                    message="Moneda en bolívares"
                                    description="El precio se convierte a USD con la tasa BCV de la fecha del escaneo."
                                />
                            )}
                        </DrawerSection>

                        <section>
                            <div className="mb-2 px-1 text-xs font-medium text-surface-500">Contexto de la visita</div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                <InfoTile icon={<Store />} label="Tienda" value={record.storeId?.name} sub={record.storeId?.client} />
                                <InfoTile icon={<User />} label="Auditor" value={record.userId?.name} sub={record.userId?.email} />
                                <InfoTile icon={<Calendar />} label="Fecha del escaneo" value={fmtVZ(record.createdAt)} />
                            </div>
                        </section>

                        {record.auditProduct ? (
                            <DrawerSection icon={<ScanBarcode />} title="Producto en catálogo">
                                <div className="truncate text-[15px] font-semibold text-surface-900">{formatPriceName(record.auditProduct)}</div>
                                <div className="mt-0.5 text-sm text-surface-500 tabular-nums">{record.auditProduct.code}</div>
                                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-surface-100 pt-3">
                                    <StatusTag tone="neutral">{record.auditProduct.category || 'Sin categoría'}</StatusTag>
                                    <StatusTag tone="neutral">{record.auditProduct.segment || 'Sin segmento'}</StatusTag>
                                    <AuditStatusBadge type="product" status={record.auditProduct.status} />
                                </div>
                            </DrawerSection>
                        ) : (
                            <div className="flex items-center gap-2 rounded-xl border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning-fg">
                                <Link2Off className="shrink-0" />
                                <span>Este escaneo no está vinculado a un producto del catálogo de auditoría.</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Drawer>
    );
}
